import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { fDateTime } from 'src/utils/format-time';

import { useTranslate } from 'src/locales';
import { DocumentType } from 'src/services/files/file.types';
import { useGetCandidateProfileQuery, useGetProfileDocumentsQuery } from 'src/services/candidates/candidates.service';
import { useGetApplicationByIdQuery, useUpdateApplicationRatingMutation } from 'src/services/applications/applications.service';

import Label from 'src/components/label';
import StarRating from 'src/components/rating/star-rating';
import DocumentViewer from 'src/components/documents/document-viewer';

import CandidateProfileEditor from 'src/sections/profile/profile-sections/profile-editor';
import CandidateProfileDocumentsView from 'src/sections/profile/documents/profile-documents';
import ApplicationScreeningSubmissionView from 'src/sections/jobs/applications/detail/screening-submissions-view';

import RecordPanelHeader from '../record-panel-header';
import { RecordMetaItem, RecordHeaderModel } from '../record-panel.types';

// ----------------------------------------------------------------------

enum TabValue {
  PROFILE = 'profile',
  RESUME = 'resume',
  DOCUMENTS = 'documents',
  SCREENING = 'screening',
}

/**
 * Application body for the record panel — the quick view opened from the applications list.
 *
 * Assembled from the same parts as the per-job ApplicationProfileDrawer (profile editor, documents,
 * resume viewer, screening submissions) but inside the shared panel chrome, so it matches the
 * candidate/contact/company views rather than introducing a second drawer style.
 *
 * No action bar: progress/reject are bulk actions on the list toolbar, and duplicating them here
 * would give the same operation two different confirmation paths.
 */
export default function ApplicationRecordView({ id }: { id: string }) {
  const { t } = useTranslate();
  const authContext = useAuthContext();

  const { data: application, isLoading: isApplicationLoading } = useGetApplicationByIdQuery(id, { skip: !id });

  const profileId = application?.attached_profile?.id;

  const { data: profile, isLoading: isProfileLoading } = useGetCandidateProfileQuery(profileId as string, {
    skip: !profileId,
  });

  const { data: profileDocuments } = useGetProfileDocumentsQuery(
    { profileId: profileId as string, params: { page: 1, per_page: 10 } },
    { skip: !profileId }
  );

  const resumeDoc = useMemo(
    () => profileDocuments?.results.find((doc) => doc.document.type === DocumentType.RESUME),
    [profileDocuments]
  );

  // Screening answers are only the managing org's to see — same gate the per-job drawer applies.
  const canSeeScreening = Boolean(application?.manager?.id && application.manager.id === authContext.organization?.id);

  const [updateRating] = useUpdateApplicationRatingMutation();

  const handleRating = useCallback(async (rating: number) => {
    try {
      await updateRating({ applicationId: id, rating }).unwrap();
    } catch {
      enqueueSnackbar(t('applications.api.update_rating.default_error'), { variant: 'error' });
    }
  }, [updateRating, id, t]);

  const [currentTab, setCurrentTab] = useState<TabValue>(TabValue.PROFILE);

  // Default to the resume once we know one exists; otherwise the profile.
  useEffect(() => {
    if (!application) return;
    setCurrentTab(resumeDoc ? TabValue.RESUME : TabValue.PROFILE);
  }, [application, resumeDoc]);

  const header: RecordHeaderModel = useMemo(() => {
    const meta: RecordMetaItem[] = [];

    if (application?.job?.title) {
      meta.push({ key: 'job', label: t('applications.table.job', { defaultValue: 'Job' }), value: application.job.title });
    }
    if (application?.created_at) {
      meta.push({
        key: 'applied',
        label: t('applications.table.created_at', { defaultValue: 'Applied' }),
        value: fDateTime(application.created_at),
      });
    }
    if (application?.stage) {
      meta.push({
        key: 'stage',
        label: t('applications.table.stage', { defaultValue: 'Stage' }),
        value: t(`enums.application_stage.${application.stage}`, { defaultValue: application.stage }),
      });
    }
    meta.push({
      key: 'rating',
      label: t('applications.table.rating', { defaultValue: 'Rating' }),
      // Editable, like every other rating surface (the per-job drawer and the candidates list).
      // The mutation invalidates this application's tag, so the panel re-reads the new value itself.
      value: <StarRating rating={application?.rating || 0} onSelectExcitementRating={handleRating} />,
    });

    const first = application?.attached_profile?.first_name ?? '';
    const last = application?.attached_profile?.last_name ?? '';

    return {
      avatarUrl: application?.attached_profile?.profile_image?.public_path,
      name: `${first} ${last}`.trim() || t('common.loading', { defaultValue: 'Loading…' }),
      subtitle: application?.attached_profile?.position_title,
      status: application?.open_status === 'new'
        ? <Label color="primary">{t('common.new')}</Label>
        : undefined,
      meta,
      contact: {
        email: application?.attached_profile?.email,
        phone: application?.attached_profile?.phone_number,
      },
    };
  }, [application, t, handleRating]);

  if (isApplicationLoading || !application) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1, py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <RecordPanelHeader model={header} />

      <Tabs
        value={currentTab}
        onChange={(_e, tab) => setCurrentTab(tab)}
        sx={{ px: 2.5, mt: 1, boxShadow: (theme) => `inset 0 -2px 0 0 ${theme.palette.divider}` }}
      >
        <Tab value={TabValue.PROFILE} label={t('profile.drawer.tabs.profile')} />
        {resumeDoc && <Tab value={TabValue.RESUME} label={t('profile.drawer.tabs.resume')} />}
        <Tab value={TabValue.DOCUMENTS} label={t('profile.drawer.tabs.documents')} />
        {canSeeScreening && (
          <Tab value={TabValue.SCREENING} label={t('profile.drawer.tabs.compliance_submissions')} />
        )}
      </Tabs>

      {currentTab === TabValue.PROFILE && (
        <Stack sx={{ px: 3, py: 2 }}>
          <CandidateProfileEditor
            profile={profile}
            isLoading={isProfileLoading || isApplicationLoading}
            mode="view"
            showNavigation={false}
            commsEntityId={application?.org_candidate?.id}
          />
        </Stack>
      )}

      {currentTab === TabValue.RESUME && (
        <Stack sx={{ px: 3, py: 2, flexGrow: 1, minHeight: 0 }}>
          {resumeDoc ? (
            <Box sx={{ height: '100%' }}>
              <DocumentViewer fileUrl={resumeDoc.document.public_path} />
            </Box>
          ) : (
            <Typography sx={{ py: 5 }} align="center" variant="body2">
              {t('profile.documents.no_resume')}
            </Typography>
          )}
        </Stack>
      )}

      {currentTab === TabValue.DOCUMENTS && (
        <Stack sx={{ px: 3, py: 2 }}>
          <CandidateProfileDocumentsView profileId={profileId as string} uploadVisible={false} guestSharing />
        </Stack>
      )}

      {currentTab === TabValue.SCREENING && canSeeScreening && (
        <Stack sx={{ px: 3, py: 2 }}>
          <ApplicationScreeningSubmissionView applicationId={id} jobId={application?.job?.id as string} />
        </Stack>
      )}
    </>
  );
}
